(ns minorodata.components.analytics-models.forms
  "Create & edit screens for analytics-model instances.

   Both wrap a SINGLE `mold-forms` form (the one source of truth) around the
   shared step bodies from `steps.cljs`:
     - `create-instance` — a linear wizard (`ui.stepper`): progress strip +
       Back/Next/Submit footer.
     - `edit-instance`   — plain tabs, where each tab saves independently (a full
       PUT — the form-model always holds the complete current state).

   Both render from one step registry (`model-steps`/`step-body`); each step's
   `:valid?` receives the shared `ctx` (model, form-id, fields, errors, timezone)."
  (:require [minorodata.app.handlers :refer [dispatch]]
            [minorodata.components.analytics-models.steps :as steps]
            [minorodata.components.connections.forms.generator :as gen]
            [mold-forms.core :as form-component]
            [mold-forms.model :refer [->form-model]]
            [re-posh.core :as re-posh]
            [ui.button :as ui.button]
            [ui.stepper :as stepper]
            [ui.tabs :as ui.tabs]))


;; -----------------------------------------------------------------------------
;; Step registry — metadata + body dispatch shared by create & edit
;; -----------------------------------------------------------------------------

(defn- model-steps
  "Step metadata shared by create (wizard) and edit (tabbed). The I/O step only
   appears when the model declares table definitions. Each `:valid?` receives the
   shared `ctx`; bodies are composed separately via `step-body`."
  [model]
  (stepper/visible-steps
   [{:key    :details
     :label  "Details"
     :valid? (fn [{:keys [model fields]}] (steps/details-valid? model fields))}
    {:key    :io
     :label  "Inputs & outputs"
     :when   (fn [{:keys [model]}] (boolean (:analytics-model-registry/table-definitions model)))
     :valid? (fn [{:keys [model fields]}] (steps/io-valid? model fields))}
    {:key    :schedule
     :label  "Schedule"
     :valid? (fn [{:keys [fields]}] (steps/schedule-valid? fields))}
    {:key   :review
     :label "Review"}]
   {:model model}))


(defn- step-body
  "Render a step's body for the given `ctx`, dispatched on step key."
  [step-key ctx]
  (case step-key
    :details  [steps/details-step ctx]
    :io       [steps/io-step ctx]
    :schedule [steps/schedule-step ctx]
    :review   [steps/review-step ctx]))


(defn- edit-steps
  "Edit tabs — the create wizard's steps minus the Review step."
  [model]
  (->> (model-steps model) (remove (comp #{:review} :key)) vec))


;; -----------------------------------------------------------------------------
;; Create — linear wizard
;; -----------------------------------------------------------------------------

(defn create-instance
  "Create-instance wizard. One shared form-model; the stepper owns step state."
  [{:keys [project-name model version on-close]}]
  (let [form-id  (keyword (str project-name "-create"))
        timezone @(re-posh/subscribe [:app.subs/timezone])]
    [form-component/form
     (->form-model
      {:id        form-id
       :clean-up  (fn [_] (dispatch [:form-model/delete [:form-model/id form-id]]))
       :on-submit (fn [form-state]
                    (let [data    (form-component/model->fields form-state)
                          missing (steps/unset-required-tables-from-data model data)]
                      (if (seq missing)
                        (dispatch [:app.events/add-snack :error (steps/required-tables-error missing)])
                        (do
                          (dispatch [:analytics-models.api/create-model
                                     (assoc (steps/->submit-params {:data         data
                                                                    :model        model
                                                                    :project-name project-name
                                                                    :version      version})
                                            :on-success
                                            (fn [instance]
                                              (dispatch [:app.events/navigate
                                                         :analytics-model-instance-details
                                                         {:instance-id (:analytics-model/model-id instance)}])))])
                          (on-close)))))
       :fields    (steps/form-fields model)})
     (fn [fields errors on-submit]
       (let [ctx   {:model model :form-id form-id :fields fields
                    :errors errors :timezone timezone}
             steps (model-steps model)]
         (into [stepper/stepper-root
                {:steps steps :ctx ctx :class "flex flex-col h-full"}
                [stepper/stepper-list {:data-cy #(str "analytics-model-step-" (name %))}]]
               (concat
                (for [{:keys [key]} steps]
                  ^{:key (name key)}
                  [stepper/stepper-content {:value key :class "flex-1 py-2"} (step-body key ctx)])
                [[stepper/stepper-footer
                  [stepper/stepper-prev {:on-close on-close}]
                  [stepper/stepper-next]
                  [stepper/stepper-submit {:on-click on-submit
                                           :data-cy  "analytics-model-create-submit-btn"}
                   "Create"]]]))))]))


;; -----------------------------------------------------------------------------
;; Edit — plain tabs, each saved independently
;; -----------------------------------------------------------------------------

(defn edit-instance
  "Edit an existing instance via plain tabs — one section per tab, each saved
   independently. Each Save is a full PUT, which versions the model (new
   model-id), so on success we navigate to the new id."
  [{:keys [id project-name model instance]}]
  (let [form-id  (keyword (str project-name "-edit-" id))
        timezone @(re-posh/subscribe [:app.subs/timezone])]
    [form-component/form
     (->form-model
      {:id        form-id
       :clean-up  (fn [_] (dispatch [:form-model/delete [:form-model/id form-id]]))
       :on-submit (fn [form-state]
                    (let [data    (form-component/model->fields form-state)
                          missing (steps/unset-required-tables-from-data model data)]
                      (if (seq missing)
                        (dispatch [:app.events/add-snack :error (steps/required-tables-error missing)])
                        (dispatch [:analytics-models.api/update-model
                                   (assoc (steps/->submit-params {:data         data
                                                                  :model        model
                                                                  :project-name project-name
                                                                  :version      (:analytics-model/version-label instance)
                                                                  :status       (:analytics-model/status instance)})
                                          :id         id
                                          :on-success (fn [updated]
                                                        (dispatch [:app.events/navigate
                                                                   :analytics-model-instance-details
                                                                   {:instance-id (:analytics-model/model-id updated)}])))]))))
       :fields    (gen/merge-initial-values (steps/form-fields model)
                                            (steps/edit-initial-values model instance))})
     (fn [fields errors on-submit]
       (let [ctx  {:model model :form-id form-id :fields fields
                   :errors errors :timezone timezone}
             tabs (edit-steps model)]
         (into [ui.tabs/tabs-root {:default-value (name (:key (first tabs)))}
                (into [ui.tabs/tabs-list {:variant :underlined}]
                      (for [{:keys [key label]} tabs]
                        ^{:key (name key)}
                        [ui.tabs/tabs-trigger {:value   (name key)
                                               :variant :underlined
                                               :data-cy (str "analytics-model-edit-tab-" (name key))}
                         label]))]
               (for [{:keys [key] :as step} tabs]
                 ^{:key (name key)}
                 [ui.tabs/tabs-content {:value (name key) :class "pt-4 flex flex-col gap-6"}
                  (step-body key ctx)
                  [:div {:class "flex justify-end border-t border-kleene-layout-border pt-4"}
                   [ui.button/button
                    {:variant  :primary
                     :on-click on-submit
                     :disabled (not (stepper/step-valid? step ctx))
                     :data-cy  (str "analytics-model-edit-save-" (name key))}
                    "Save"]]]))))]))
